import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type SubmitEvent, useState } from 'react'

import type { Language } from '@/bindings/Language'
import type { MissingTranslation } from '@/bindings/MissingTranslation'
import type { RestaurantContext } from '@/bindings/RestaurantContext'
import type { RestaurantSettings } from '@/bindings/RestaurantSettings'
import type { UpdateRestaurantSettingsRequest } from '@/bindings/UpdateRestaurantSettingsRequest'
import { AccentScope } from '@/components/accent'
import { Button } from '@/components/button'
import { Checkbox, Field, Input, Select, Textarea } from '@/components/field'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { backOfficeKeys, languageCoverageQuery, restaurantPath } from '@/features/back-office/api'
import { type NameDraft, namesFrom, toLocalizedText } from '@/features/back-office/name-draft'
import { useManagedRestaurant } from '@/features/back-office/scope'
import { request } from '@/lib/api'
import { LANGUAGES, LANGUAGE_NAMES } from '@/lib/i18n/languages'
import { type Translate, useT } from '@/lib/i18n/translate'
import { canManageRestaurant } from '@/lib/roles'

/** Back-office restaurant page (PRD): name, color, intro, language setting. Manager and above. */
export function RestaurantSettingsPage() {
  const t = useT()
  const { context } = useManagedRestaurant()

  return (
    <Page width="md" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">{t('settings.title')}</h1>
      </div>
      {canManageRestaurant(context.role) ? (
        <SettingsForm key={context.settings.id} context={context} />
      ) : (
        <p role="alert" className="text-muted text-sm">
          {t('settings.managerOnly')}
        </p>
      )}
    </Page>
  )
}

interface Draft {
  name: string
  color: string
  intro: NameDraft
  mainLanguage: Language
  secondary: readonly Language[]
}

function draftFrom(settings: RestaurantSettings): Draft {
  return {
    name: settings.name,
    color: settings.color,
    intro: namesFrom(settings.intro),
    mainLanguage: settings.main_language,
    secondary: settings.secondary_languages,
  }
}

function toRequest(draft: Draft): UpdateRestaurantSettingsRequest {
  return {
    name: draft.name.trim(),
    color: draft.color,
    intro: toLocalizedText(draft.intro),
    main_language: draft.mainLanguage,
    secondary_languages: draft.secondary.filter((language) => language !== draft.mainLanguage),
  }
}

function missingLabel(item: MissingTranslation, t: Translate): string {
  switch (item.kind) {
    case 'intro':
      return t('settings.missing.intro')
    case 'menu_category':
      return t('settings.missing.menuCategory', { name: item.name })
    case 'menu_item':
      return t('settings.missing.menuItem', { name: item.name })
    case 'announcement':
      return t('settings.missing.announcement', { name: item.name })
  }
}

function SettingsForm({ context }: { context: RestaurantContext }) {
  const t = useT()
  const queryClient = useQueryClient()
  const { settings } = context
  const [draft, setDraft] = useState(() => draftFrom(settings))
  const [saved, setSaved] = useState(false)
  const patch = (changes: Partial<Draft>) => {
    setSaved(false)
    setDraft((current) => ({ ...current, ...changes }))
  }

  const save = useMutation({
    mutationFn: () =>
      request<RestaurantSettings>(restaurantPath(settings.id, '/settings'), { method: 'PUT', body: toRequest(draft) }),
    onSuccess: (next) => {
      // The shell re-themes and renames from this same cache entry, and the
      // form's baseline (`settings`) moves with it; the draft takes the
      // server's normalized copy.
      queryClient.setQueryData(backOfficeKeys.context(context.settings.slug), { ...context, settings: next })
      void queryClient.invalidateQueries({ queryKey: backOfficeKeys.mine })
      setDraft(draftFrom(next))
      setSaved(true)
    },
  })

  function submit(event: SubmitEvent) {
    event.preventDefault()
    save.mutate()
  }

  /** The old main stays available as a secondary, so its text keeps a field (T4.2). */
  function chooseMain(next: Language) {
    const previous = draft.mainLanguage
    if (next === previous) return
    const secondary = draft.secondary.filter((language) => language !== next)
    patch({ mainLanguage: next, secondary: secondary.includes(previous) ? secondary : [...secondary, previous] })
  }

  function toggleSecondary(language: Language, on: boolean) {
    patch({ secondary: on ? [...draft.secondary, language] : draft.secondary.filter((other) => other !== language) })
  }

  const shownLanguages = [draft.mainLanguage, ...draft.secondary]
  const mainChanged = draft.mainLanguage !== settings.main_language

  return (
    <AccentScope color={draft.color}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Card className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold">{t('settings.general')}</h2>
          <Field label={t('restaurants.name')}>
            <Input required value={draft.name} onChange={(e) => patch({ name: e.target.value })} />
          </Field>
          <Field label={t('restaurants.color')} hint={t('settings.colorHint')}>
            <Input
              type="color"
              value={draft.color}
              onChange={(e) => patch({ color: e.target.value })}
              className="h-11 w-24 p-1"
            />
          </Field>
        </Card>

        <Card className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold">{t('settings.languagesTitle')}</h2>
          <Field label={t('restaurants.mainLanguage')} hint={t('settings.mainLanguageHint')}>
            <Select value={draft.mainLanguage} onChange={(e) => chooseMain(e.target.value as Language)}>
              {LANGUAGES.map((language) => (
                <option key={language} value={language}>
                  {LANGUAGE_NAMES[language]}
                </option>
              ))}
            </Select>
          </Field>
          <fieldset className="flex flex-col gap-1">
            <legend className="mb-1 text-sm font-medium">{t('restaurants.secondaryLanguages')}</legend>
            {LANGUAGES.filter((language) => language !== draft.mainLanguage).map((language) => (
              <Checkbox
                key={language}
                label={LANGUAGE_NAMES[language]}
                checked={draft.secondary.includes(language)}
                onChange={(e) => toggleSecondary(language, e.target.checked)}
              />
            ))}
          </fieldset>
          {mainChanged && <MainChangeWarning restaurantId={settings.id} draft={draft} />}
        </Card>

        <Card className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold">{t('settings.introTitle')}</h2>
          <p className="text-muted text-sm">{t('settings.introHint')}</p>
          {shownLanguages.map((language) => (
            <Field
              key={language}
              label={t('settings.introFor', { language: LANGUAGE_NAMES[language] })}
              hint={language === draft.mainLanguage ? t('settings.introMain') : t('common.optional')}
            >
              <Textarea
                rows={4}
                value={draft.intro[language]}
                onChange={(e) => patch({ intro: { ...draft.intro, [language]: e.target.value } })}
              />
            </Field>
          ))}
        </Card>

        <FormError error={save.error} />
        <div className="flex items-center gap-3">
          <Button type="submit" loading={save.isPending}>
            {t('common.save')}
          </Button>
          {saved && (
            <p role="status" className="text-muted text-sm">
              {t('settings.saved')}
            </p>
          )}
        </div>
      </form>
    </AccentScope>
  )
}

/**
 * T4.4: changing main never blocks — list what has no text in the new main
 * and will fall back. The intro verdict comes from the form itself (unsaved
 * edits count); everything else from the backend's coverage check.
 */
function MainChangeWarning({ restaurantId, draft }: { restaurantId: string; draft: Draft }) {
  const t = useT()
  const coverage = useQuery(languageCoverageQuery(restaurantId, draft.mainLanguage))
  const language = LANGUAGE_NAMES[draft.mainLanguage]

  const introFilled = LANGUAGES.some((other) => draft.intro[other].trim())
  // Keyed by kind + id: two items may share a name.
  const items = [
    ...(introFilled && !draft.intro[draft.mainLanguage].trim() ? [{ key: 'intro', label: t('settings.missing.intro') }] : []),
    ...(coverage.data?.missing ?? []).flatMap((item) =>
      item.kind === 'intro' ? [] : [{ key: `${item.kind}:${item.id}`, label: missingLabel(item, t) }],
    ),
  ]

  return (
    <div role="status" className="border-warning flex flex-col gap-2 rounded-lg border p-3 text-sm">
      <p className="font-medium">{t('settings.mainChangeTitle', { language })}</p>
      {coverage.isPending ? (
        <p className="text-muted">{t('settings.mainChangeChecking', { language })}</p>
      ) : items.length === 0 ? (
        <p className="text-muted">{t('settings.mainChangeComplete', { language })}</p>
      ) : (
        <>
          <p className="text-muted">{t('settings.mainChangeMissing', { language })}</p>
          <ul className="list-disc pl-5">
            {items.map((item) => (
              <li key={item.key}>{item.label}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}
